import { ErrorBanner, errorText } from '../components/ErrorBanner';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AlertTriangle, CheckCircle2, ChevronRight, Loader2 } from 'lucide-react';
import { projectApi } from '../mocks/projectApi';
import { archiveApi } from '../mocks/archiveApi';
import { scheduleApi } from '../mocks/scheduleApi';
import { PROJECT_STAGES, Project, ProjectStage, STAGE_STYLE } from '../types/project';
import { ArchiveDoc } from '../types/archive';
import { EmailLog, Schedule } from '../types/schedule';

/**
 * 대시보드 — 「지금 손봐야 하는 것」이 맨 위다.
 *
 * 설계 문서의 규칙을 따른다: **배지는 「내가 처리할 것」만 센다. 전체 건수가
 * 아니다.** 단계별 건수와 마감 순 목록은 그 아래에 둔다.
 *
 * 시안(`docs/02-design/canvas/Main.dc.html`)은 단계별 건수 카드와 전체 목록
 * 표였는데, 그건 프로젝트 화면이 이미 한다(요약 줄 · 보드 · 표). 여기서만 볼 수
 * 있는 것을 위로 올렸다 — 내버려 두면 어긋나는 것들이다.
 *
 * ⚠️ **새 공고 건수는 나라장터를 다시 부르지 않는다.** 조회가 8~30초라
 *    대시보드를 열 때마다 부르면 화면이 멈춘다. 수집 기준이 마지막으로 보낸
 *    메일의 건수를 읽는다 — 「그때 몇 건이었나」이지 지금 몇 건인지가 아니다.
 */
function daysLeft(v: string): number | null {
  const d = new Date(v.replace(/\//g, '-'));
  if (isNaN(d.getTime())) return null;
  return Math.ceil((d.getTime() - Date.now()) / 86_400_000);
}

function daysAgo(v: string): number {
  return Math.floor((Date.now() - new Date(v).getTime()) / 86_400_000);
}

function agoText(v: string): string {
  const d = daysAgo(v);
  if (d <= 0) return '오늘';
  if (d === 1) return '어제';
  return `${d}일 전`;
}

const formatPrice = (v: number | null): string => {
  if (!v) return '—';
  if (v >= 100_000_000) return `${(v / 100_000_000).toFixed(1)}억`;
  return `${Math.round(v / 10_000).toLocaleString()}만`;
};

/** 손봐야 하는 것 한 줄 */
interface Todo {
  key: string;
  kind: string;
  tone: 'red' | 'amber';
  title: string;
  note: string;
  to: string;
}

export const DashboardPage: React.FC = () => {
  const navigate = useNavigate();
  const [projects, setProjects] = useState<Project[]>([]);
  const [docs, setDocs] = useState<ArchiveDoc[]>([]);
  const [schedules, setSchedules] = useState<Schedule[]>([]);
  /** 발송 로그는 스케줄마다 따로 받는다 — 목록에 안 실려 온다 */
  const [logsById, setLogsById] = useState<Record<number, EmailLog[]>>({});
  const [loading, setLoading] = useState(true);
  /* 못 불러오면 「밀린 것이 없습니다」로 보이던 것을 막는다(#107) */
  const [loadError, setLoadError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const [p, d, s] = await Promise.all([
        projectApi.list(),
        archiveApi.list(),
        scheduleApi.list(),
      ]);
      setProjects(p);
      setDocs(d);
      setSchedules(s);

      const entries = await Promise.all(
        s.map(async (x) => {
          try {
            const data = await scheduleApi.logs(x.id);
            return [x.id, (data.items ?? []) as EmailLog[]] as const;
          } catch {
            return [x.id, [] as EmailLog[]] as const;
          }
        }),
      );
      setLogsById(Object.fromEntries(entries));
    } catch (e) {
      setLoadError(`대시보드를 불러오지 못했습니다 — ${errorText(e)}. 아래 숫자는 믿지 마세요`);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const todos = useMemo<Todo[]>(() => {
    const out: Todo[] = [];

    /* 1. PPE 미반영 — 제일 위험하다. 수주했는데 admin 에 없으면 수익성이 어긋난다.
       종료 건은 뺀다 — 드라이브에서 들인 과거 수주 47건(2025년까지)은 admin 이 생기기 전 일이다 */
    projects
      .filter((p) => p.outcome === '수주' && p.stage !== '종료' && !p.ppeRegisteredAt)
      .forEach((p) => out.push({
        key: `ppe-${p.id}`,
        kind: 'PPE 미반영',
        tone: 'red',
        title: p.name,
        note: p.decidedAt ? `수주 ${agoText(p.decidedAt)}` : '수주',
        // 반영 버튼은 수주 판정 칸에 있다
        to: `/projects/${p.id}?step=gate`,
      }));

    /* 2. 판정 대기 — 제안서 단계인데 마감이 지났다. 결과가 나왔을 텐데 안 적었다 */
    projects
      .filter((p) => p.stage === '제안서' && !p.outcome)
      .filter((p) => (daysLeft(p.dueAt) ?? 1) < 0)
      .forEach((p) => out.push({
        key: `gate-${p.id}`,
        kind: '판정 대기',
        tone: 'amber',
        title: p.name,
        note: `마감 ${agoText(p.dueAt)}`,
        to: `/projects/${p.id}?step=gate`,
      }));

    /* 3. 마감 임박 — 3일 이내이고 아직 판정 전인 것 */
    projects
      .filter((p) => !p.outcome && p.stage !== '종료')
      .filter((p) => {
        const d = daysLeft(p.dueAt);
        return d !== null && d >= 0 && d <= 3;
      })
      .forEach((p) => out.push({
        key: `due-${p.id}`,
        kind: `마감 D-${daysLeft(p.dueAt)}`,
        tone: 'red',
        title: p.name,
        note: `${p.stage} 단계`,
        to: `/projects/${p.id}`,
      }));

    /* 4. 검수 필요 — AI 가 뽑은 값을 아직 아무도 안 봤다. 여러 건이라 한 줄로 묶는다 */
    const unreviewed = docs.filter((d) => d.extracted && !d.reviewed);
    if (unreviewed.length > 0) {
      out.push({
        key: 'review',
        kind: '검수 필요',
        tone: 'amber',
        title: `아카이브 문서 ${unreviewed.length}건`,
        note: 'AI 가 뽑은 값을 아직 확인하지 않았습니다',
        to: '/archive',
      });
    }

    /* 5. 멈춘 수집 기준 — 꺼 둔 것은 알고 끈 것이니 세지 않는다 */
    schedules
      .filter((s) => s.isActive)
      .filter((s) => {
        const last = [...(logsById[s.id] ?? [])].sort((a, b) => b.sentAt.localeCompare(a.sentAt))[0];
        return last && last.status !== 'success';
      })
      .forEach((s) => out.push({
        key: `sched-${s.id}`,
        kind: '수집 실패',
        tone: 'amber',
        title: s.name,
        note: '마지막 발송이 실패했습니다',
        to: '/schedules',
      }));

    const order = { red: 0, amber: 1 };
    return out.sort((a, b) => order[a.tone] - order[b.tone]);
  }, [projects, docs, schedules, logsById]);

  /** 마감 순 — 판정 전이고 마감이 남은 것 */
  const upcoming = useMemo(
    () => projects
      .filter((p) => !p.outcome && p.stage !== '종료')
      .map((p) => ({ p, d: daysLeft(p.dueAt) }))
      .filter((x) => x.d !== null && x.d >= 0)
      .sort((a, b) => (a.d as number) - (b.d as number)),
    [projects],
  );

  /** 마지막 발송에서 몇 건을 물어왔나 */
  const lastHarvest = useMemo(
    () => schedules
      .map((s) => ({
        name: s.name,
        log: [...(logsById[s.id] ?? [])].sort((a, b) => b.sentAt.localeCompare(a.sentAt))[0],
      }))
      .filter((x): x is { name: string; log: EmailLog } => Boolean(x.log))
      .sort((a, b) => b.log.sentAt.localeCompare(a.log.sentAt)),
    [schedules, logsById],
  );

  const countOf = (s: ProjectStage) => projects.filter((p) => p.stage === s).length;
  const today = new Date().toLocaleDateString('ko-KR', {
    year: 'numeric', month: 'long', day: 'numeric', weekday: 'short',
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center gap-2 py-24 text-sm text-slate-500">
        <Loader2 size={16} className="animate-spin" /> 불러오는 중...
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-8 max-w-[1400px]">
      <div className="flex items-baseline justify-between gap-4 mb-5">
        <h1 className="text-xl font-bold text-slate-900 flex items-center gap-1.5">
          대시보드
        </h1>
        <span className="text-xs text-slate-400">{today}</span>
      </div>
      <ErrorBanner message={loadError} onRetry={load} className="mb-4" />

      {/* 1. 손봐야 하는 것 */}
      <section className="bg-white border border-slate-200 rounded-lg mb-4">
        <div className="flex items-center justify-between px-4 py-2.5 border-b border-slate-100">
          <h2 className="flex items-center gap-1.5 text-sm font-semibold text-slate-900">
            <AlertTriangle size={14} className={todos.length ? 'text-red-600' : 'text-slate-300'} />
            지금 손봐야 하는 것
          </h2>
          <span className="text-sm font-bold tabular-nums text-slate-900">{todos.length}</span>
        </div>

        {todos.length === 0 ? (
          <p className="flex items-center justify-center gap-1.5 py-10 text-sm text-slate-500">
            <CheckCircle2 size={14} className="text-emerald-600" /> 밀린 것이 없습니다.
          </p>
        ) : (
          <div>
            {todos.map((t) => (
              <button
                key={t.key}
                onClick={() => navigate(t.to)}
                className="w-full flex items-center gap-3 px-4 py-2.5 border-b border-slate-50 last:border-b-0 hover:bg-slate-50 text-left transition"
              >
                <span className={`rounded-full px-2 py-0.5 text-xs font-semibold flex-shrink-0 ${
                  t.tone === 'red' ? 'bg-red-50 text-red-600' : 'bg-amber-50 text-amber-700'
                }`}>
                  {t.kind}
                </span>
                <span className="flex-1 min-w-0 text-sm text-slate-900 truncate">{t.title}</span>
                <span className="text-xs text-slate-400 flex-shrink-0 hidden sm:block">{t.note}</span>
                <ChevronRight size={14} className="text-slate-300 flex-shrink-0" />
              </button>
            ))}
          </div>
        )}
      </section>

      {/* 2. 단계별 건수 */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2 mb-4">
        {PROJECT_STAGES.map((s) => (
          <Link
            key={s}
            to="/projects"
            className="bg-white border border-slate-200 rounded-lg px-3.5 py-2.5 hover:border-slate-300 transition"
          >
            <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${STAGE_STYLE[s]}`}>{s}</span>
            <div className="mt-1 text-xl font-bold text-slate-900 tabular-nums">{countOf(s)}</div>
          </Link>
        ))}
      </div>

      <div className="grid gap-4 lg:grid-cols-[minmax(0,1.6fr)_minmax(0,1fr)]">
        {/* 3. 마감 순 */}
        <section className="bg-white border border-slate-200 rounded-lg">
          <div className="flex items-center justify-between px-4 py-2.5 border-b border-slate-100">
            <h2 className="text-sm font-semibold text-slate-900">다가오는 마감</h2>
            <Link to="/projects" className="text-xs text-indigo-600 hover:underline">프로젝트</Link>
          </div>
          {upcoming.length === 0 ? (
            <p className="py-10 text-center text-sm text-slate-500">마감이 남은 프로젝트가 없습니다.</p>
          ) : (
            <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <tbody>
                {upcoming.map(({ p, d }) => (
                  <tr
                    key={p.id}
                    onClick={() => navigate(`/projects/${p.id}`)}
                    className="border-b border-slate-50 last:border-b-0 hover:bg-slate-50 cursor-pointer"
                  >
                    <td className="pl-4 py-2.5 w-16">
                      <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${
                        (d as number) <= 7 ? 'bg-red-50 text-red-600' : 'bg-slate-100 text-slate-500'
                      }`}>
                        D-{d}
                      </span>
                    </td>
                    <td className="py-2.5 pr-2">
                      <div className="text-slate-900 truncate">{p.name}</div>
                      <div className="text-xs text-slate-400">{p.clientName}</div>
                    </td>
                    <td className="py-2.5 pr-2 text-right whitespace-nowrap text-xs font-medium text-slate-700 tabular-nums">
                      {formatPrice(p.proposalPrice)}
                    </td>
                    <td className="py-2.5 pr-4 text-right whitespace-nowrap">
                      <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${STAGE_STYLE[p.stage]}`}>
                        {p.stage}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          )}
        </section>

        {/* 4. 마지막 수집 */}
        <section className="bg-white border border-slate-200 rounded-lg">
          <div className="flex items-center justify-between px-4 py-2.5 border-b border-slate-100">
            <h2 className="text-sm font-semibold text-slate-900">마지막 수집</h2>
            <Link to="/bids" className="text-xs text-indigo-600 hover:underline">공고</Link>
          </div>
          {lastHarvest.length === 0 ? (
            <p className="py-10 text-center text-sm text-slate-500">보낸 메일이 없습니다.</p>
          ) : (
            <>
              <div className="px-4 py-2 divide-y divide-slate-50">
                {lastHarvest.map(({ name, log }) => (
                  <div key={name} className="flex items-center justify-between gap-2 py-2">
                    <div className="min-w-0">
                      <div className="text-sm text-slate-900 truncate">{name}</div>
                      <div className="text-xs text-slate-400">{agoText(log.sentAt)}</div>
                    </div>
                    <span className={`text-sm font-bold tabular-nums flex-shrink-0 ${
                      log.status !== 'success' ? 'text-red-600' : log.bidCount === 0 ? 'text-slate-300' : 'text-slate-900'
                    }`}>
                      {log.status !== 'success' ? '실패' : `${log.bidCount}건`}
                    </span>
                  </div>
                ))}
              </div>
              <p className="px-4 pb-3 text-xs text-slate-400 leading-relaxed">
                마지막으로 보낸 메일의 건수입니다 — 지금 몇 건인지는 공고에서 조회합니다.
              </p>
            </>
          )}
        </section>
      </div>

    </div>
  );
};
